/**
 * Quiz Categories Management Screen
 * Create and manage quiz categories
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { QuizCategory } from "@/modules/quiz";

/**
 * Available icons for categories
 */
const AVAILABLE_ICONS = [
  "bulb",
  "people",
  "football",
  "film",
  "musical-notes",
  "book",
  "flask",
  "globe",
  "heart",
  "star",
  "airplane",
  "car",
  "pizza",
  "game-controller",
  "trophy",
  "school",
  "home",
  "paw",
  "leaf",
  "sunny",
];

/**
 * Available colors
 */
const AVAILABLE_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#EF4444", // Red
  "#84CC16", // Lime
  "#F97316", // Orange
];

export default function CategoriesScreen() {
  const router = useRouter();
  const colors = useThemeColors();

  // State
  const [categories, setCategories] = useState<QuizCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<QuizCategory | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("bulb");
  const [color, setColor] = useState("#3B82F6");

  /**
   * Fetch categories
   */
  const fetchCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from("quiz_categories")
      .select("*")
      .order("sort_order", { ascending: true });

    if (data) {
      setCategories(data as QuizCategory[]);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * Open modal for new category
   */
  const handleAddCategory = () => {
    setEditingCategory(null);
    setName("");
    setDescription("");
    setIcon("bulb");
    setColor("#3B82F6");
    setShowModal(true);
  };

  /**
   * Open modal for editing
   */
  const handleEditCategory = (category: QuizCategory) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description || "");
    setIcon(category.icon);
    setColor(category.color);
    setShowModal(true);
  };

  /**
   * Save category
   */
  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Fout", "Vul een naam in");
      return;
    }

    setIsSaving(true);

    try {
      if (editingCategory) {
        // Update
        const { error } = await supabase
          .from("quiz_categories")
          .update({
            name: name.trim(),
            description: description.trim() || null,
            icon,
            color,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingCategory.id);

        if (error) throw new Error(error.message);

        setCategories((prev) =>
          prev.map((c) =>
            c.id === editingCategory.id
              ? { ...c, name: name.trim(), description: description.trim(), icon, color }
              : c
          )
        );
      } else {
        // Create
        const { data, error } = await supabase
          .from("quiz_categories")
          .insert({
            name: name.trim(),
            description: description.trim() || null,
            icon,
            color,
            sort_order: categories.length,
            is_active: true,
          })
          .select()
          .single();

        if (error) throw new Error(error.message);

        setCategories((prev) => [...prev, data as QuizCategory]);
      }

      setShowModal(false);
    } catch (error) {
      console.error("Error saving category:", error);
      Alert.alert("Fout", "Kon categorie niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Toggle category active state
   */
  const handleToggleActive = async (category: QuizCategory) => {
    const { error } = await supabase
      .from("quiz_categories")
      .update({ is_active: !category.is_active })
      .eq("id", category.id);

    if (error) {
      Alert.alert("Fout", "Kon categorie niet bijwerken");
      return;
    }

    setCategories((prev) =>
      prev.map((c) =>
        c.id === category.id ? { ...c, is_active: !c.is_active } : c
      )
    );
  };

  /**
   * Delete category
   */
  const handleDelete = (category: QuizCategory) => {
    Alert.alert(
      "Categorie Verwijderen",
      `Weet je zeker dat je "${category.name}" wilt verwijderen?\n\nVragen in deze categorie worden niet verwijderd, maar verliezen hun categorie.`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const { error } = await supabase
              .from("quiz_categories")
              .delete()
              .eq("id", category.id);

            if (error) {
              Alert.alert("Fout", "Kon categorie niet verwijderen");
              return;
            }

            setCategories((prev) => prev.filter((c) => c.id !== category.id));
          },
        },
      ]
    );
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            Categorieën
          </Text>
        </View>

        <TouchableOpacity
          onPress={handleAddCategory}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.primary }}
        >
          <Ionicons name="add" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : categories.length === 0 ? (
          <View className="py-12 items-center">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons name="folder-open" size={40} color={colors.primary} />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen categorieën
            </Text>
            <Text style={{ color: colors.textSecondary }}>
              Maak je eerste categorie aan
            </Text>
          </View>
        ) : (
          categories.map((category) => (
            <TouchableOpacity
              key={category.id}
              onPress={() => handleEditCategory(category)}
              activeOpacity={0.7}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: category.is_active ? colors.border : colors.error + "30",
                opacity: category.is_active ? 1 : 0.6,
              }}
            >
              <View
                className="w-12 h-12 rounded-xl items-center justify-center"
                style={{ backgroundColor: `${category.color}20` }}
              >
                <Ionicons
                  name={category.icon as any}
                  size={24}
                  color={category.color}
                />
              </View>
              <View className="flex-1 ml-3">
                <Text className="font-bold" style={{ color: colors.text }}>
                  {category.name}
                </Text>
                {category.description && (
                  <Text
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                    numberOfLines={1}
                  >
                    {category.description}
                  </Text>
                )}
                {!category.is_active && (
                  <Text className="text-xs mt-1" style={{ color: colors.error }}>
                    Inactief
                  </Text>
                )}
              </View>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => handleToggleActive(category)}
                  className="w-8 h-8 rounded-full items-center justify-center mr-2"
                  style={{
                    backgroundColor: category.is_active
                      ? "#10B98120"
                      : colors.error + "20",
                  }}
                >
                  <Ionicons
                    name={category.is_active ? "eye" : "eye-off"}
                    size={16}
                    color={category.is_active ? "#10B981" : colors.error}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleDelete(category)}
                  className="w-8 h-8 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.error + "20" }}
                >
                  <Ionicons name="trash" size={16} color={colors.error} />
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          ))
        )}

        <View className="h-24" />
      </ScrollView>

      {/* Add/Edit Modal */}
      <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
          {/* Modal Header */}
          <View className="px-4 py-4 flex-row items-center justify-between">
            <TouchableOpacity
              onPress={() => setShowModal(false)}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface }}
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              {editingCategory ? "Bewerk Categorie" : "Nieuwe Categorie"}
            </Text>
            <TouchableOpacity
              onPress={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-xl"
              style={{ backgroundColor: isSaving ? colors.border : colors.primary }}
            >
              {isSaving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text className="font-bold text-white">Opslaan</Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
            {/* Preview */}
            <View className="items-center py-6">
              <View
                className="w-20 h-20 rounded-2xl items-center justify-center"
                style={{ backgroundColor: `${color}20` }}
              >
                <Ionicons name={icon as any} size={40} color={color} />
              </View>
              <Text className="mt-3 font-bold text-lg" style={{ color: colors.text }}>
                {name || "Categorie Naam"}
              </Text>
            </View>

            {/* Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Naam *
              </Text>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="Categorie naam"
                placeholderTextColor={colors.textTertiary}
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.surface,
                  color: colors.text,
                }}
              />
            </View>

            {/* Description */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Beschrijving (optioneel)
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                placeholder="Korte beschrijving"
                placeholderTextColor={colors.textTertiary}
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.surface,
                  color: colors.text,
                }}
              />
            </View>

            {/* Icon Selection */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Icoon
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_ICONS.map((iconName) => (
                  <TouchableOpacity
                    key={iconName}
                    onPress={() => setIcon(iconName)}
                    className="w-12 h-12 rounded-xl items-center justify-center m-1"
                    style={{
                      backgroundColor: icon === iconName ? `${color}30` : colors.surface,
                      borderWidth: 2,
                      borderColor: icon === iconName ? color : colors.border,
                    }}
                  >
                    <Ionicons
                      name={iconName as any}
                      size={22}
                      color={icon === iconName ? color : colors.textSecondary}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Color Selection */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_COLORS.map((colorOption) => (
                  <TouchableOpacity
                    key={colorOption}
                    onPress={() => setColor(colorOption)}
                    className="w-12 h-12 rounded-xl items-center justify-center m-1"
                    style={{
                      backgroundColor: colorOption,
                      borderWidth: 3,
                      borderColor:
                        color === colorOption ? colors.text : "transparent",
                    }}
                  >
                    {color === colorOption && (
                      <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View className="h-24" />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

